import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApiClient } from '@/hooks/useApiClient';
import { TraceabilityApiClient } from '@/features/traceability/api/TraceabilityApiClient';
import { errorResult } from '@/lib/http/runJob';
import { JobRequestHandler } from '@/lib/http/JobRequestHandler';
import type { Result } from '@/lib/http/HttpClient';
import type { AllToolsResponse } from '@/features/traceability/api/TraceabilityResponseType';
import { type Tool } from '@/features/traceability/type/Tool';
import { ReferenceAnalyticsLayout } from '../components/analytics/ReferenceAnalyticsLayout';
import type { AnalyticsErrorDetails } from '../types';
import { useTranslation } from '@/hooks/useTranslation';
import { useApiCall } from '@/hooks/useApiCall';
import { buildLeaderboardFromTools } from '../utils/tool';
import { sanitizeReference } from '../utils/reference';
import { StorageService } from '@/lib/StorageService';

const LAST_SELECTED_REFERENCE_KEY = 'analytics_last_selected_reference';
const storage = new StorageService();

export function Analytics() {
  const http = useApiClient();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [tools, setTools] = useState<Tool[]>([]);
  const [references, setReferences] = useState<string[]>([]);
  const [reference, setReference] = useState<string>('');
  const [errorDetails, setErrorDetails] = useState<AnalyticsErrorDetails | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const preservedReferenceRef = useRef<string | null>(null);

  const traceabilityApiClient = useMemo(
    () => (http ? new TraceabilityApiClient(http) : null),
    [http]
  );

  // Fetch tools data using JobRequestHandler wrapped in useApiCall
  const fetchTools = useCallback(
    async (signal: AbortSignal) => {
      if (!traceabilityApiClient) {
        return errorResult('TraceabilityApiClient not initialized', 'InitializationError');
      }

      return new Promise<Result<AllToolsResponse>>((resolve) => {
        let settled = false;

        const handler = new JobRequestHandler<AllToolsResponse>({
          initFn: () => traceabilityApiClient.toolReadAll(undefined, { signal }),
          statusFn: (jobId) => traceabilityApiClient.toolsStatus({ jobId }, { signal }),
          signal,
          setResult: (result) => {
            if (settled || !result) {
              return;
            }

            settled = true;

            if (!result.ok) {
              console.error('Failed to load tools', result);
              const errorInfo: AnalyticsErrorDetails = {
                name: result.name ?? null,
                code: result.code ? String(result.code) : null,
                message: result.message ?? t('analytics.dashboard.error.unknown'),
                timestamp: result.timestamp ?? new Date().toISOString(),
              };
              setErrorDetails(errorInfo);
              resolve({
                ok: false,
                code: result.code ?? 500,
                name: result.name ?? 'UnknownError',
                message: result.message ?? t('analytics.dashboard.error.loadFailure'),
                timestamp: result.timestamp ?? new Date().toISOString(),
              });
              return;
            }

            // Success - resolve with the result
            setErrorDetails(null);
            resolve(result);
          },
        });

        handler.start().catch((err) => {
          if (settled) return;
          settled = true;
          console.error('Failed to load tools', err);
          const errorInfo: AnalyticsErrorDetails = {
            name: err instanceof Error ? err.name : null,
            message: err instanceof Error ? err.message : String(err),
            code:
              err && typeof err === 'object' && 'code' in err && err.code != null
                ? String((err as { code: unknown }).code)
                : null,
            timestamp: new Date().toISOString(),
          };
          setErrorDetails(errorInfo);
          resolve({
            ok: false,
            code: 500,
            name: err instanceof Error ? err.name : 'UnknownError',
            message: err instanceof Error ? err.message : String(err),
            timestamp: new Date().toISOString(),
          });
        });
      });
    },
    [traceabilityApiClient, t]
  );

  // Use the useApiCall hook to manage loading, error, and data states
  const { error, isLoading } = useApiCall<AllToolsResponse>({
    apiFn: fetchTools,
    deps: [refreshTick], // Changed from [fetchTools, refreshTick] to avoid infinite loop
    immediate: !!traceabilityApiClient,
    onSuccess: (data) => {
      const fetchedTools = data.tools.filter((tool): tool is Tool => !!tool);

      setErrorDetails(null);
      setTools(
        fetchedTools.map((tool) => ({
          ...tool,
          traces: Array.isArray(tool.traces) ? tool.traces : [],
        }))
      );
    },
    onError: () => {
      setTools([]);
      setReferences([]);
      setReference('');
      preservedReferenceRef.current = null;
    },
  });

  // Update leaderboard and references when tools change
  useEffect(() => {
    if (!tools.length) {
      setReferences([]);
      setReference('');
      preservedReferenceRef.current = null;
      return;
    }

    const dataset = buildLeaderboardFromTools(tools);
    setReferences(dataset.labels);
    setReference((previous) => {
      const normalizedPreserved = preservedReferenceRef.current
        ? sanitizeReference(preservedReferenceRef.current)
        : null;
      if (normalizedPreserved && dataset.labels.includes(normalizedPreserved)) {
        preservedReferenceRef.current = normalizedPreserved;
        return normalizedPreserved;
      }

      const normalizedPrevious = sanitizeReference(previous);
      if (normalizedPrevious && dataset.labels.includes(normalizedPrevious)) {
        preservedReferenceRef.current = normalizedPrevious;
        return normalizedPrevious;
      }

      // Try to restore last selected reference from localStorage
      const storedReference = storage.getPreference(LAST_SELECTED_REFERENCE_KEY);
      const normalizedStored = sanitizeReference(storedReference);
      if (normalizedStored && dataset.labels.includes(normalizedStored)) {
        preservedReferenceRef.current = normalizedStored;
        return normalizedStored;
      }

      const fallback = dataset.labels[0] ?? '';
      preservedReferenceRef.current = fallback ? fallback : null;
      return fallback;
    });
  }, [tools]);

  const handleRefresh = useCallback(() => {
    preservedReferenceRef.current = sanitizeReference(reference);
    setErrorDetails(null);
    setRefreshTick((value) => value + 1);
  }, [reference]);

  const handleReferenceChange = useCallback((value: string | null) => {
    const sanitized = sanitizeReference(value);
    preservedReferenceRef.current = sanitized;
    setReference(sanitized ?? '');
    // Save to localStorage for persistence across sessions
    if (sanitized) {
      storage.setPreference(LAST_SELECTED_REFERENCE_KEY, sanitized);
    } else {
      storage.removePreference(LAST_SELECTED_REFERENCE_KEY);
    }
  }, []);

  const handleCompareClick = useCallback(() => {
    // Navigate to comparison page with current reference as parameter
    const encodedReference = reference ? encodeURIComponent(reference) : '';
    const targetUrl = `/analytics/comparison${encodedReference ? `/${encodedReference}` : ''}`;
    navigate(targetUrl);
  }, [navigate, reference]);

  return (
    <ReferenceAnalyticsLayout
      references={references}
      selectedReference={reference}
      onReferenceChange={handleReferenceChange}
      onRefresh={handleRefresh}
      onCompareClick={handleCompareClick}
      isLoading={isLoading}
      error={error}
      errorDetails={errorDetails}
      tools={tools}
    />
  );
}
